import type { CompanyData, FunnelMonthlyRow } from "./types";
import { formatCurrency, formatDateRange, formatNumber, formatPercentage } from "./utils";

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (char) => {
    if (char === "&") return "&amp;";
    if (char === "<") return "&lt;";
    if (char === ">") return "&gt;";
    if (char === '"') return "&quot;";
    if (char === "'") return "&#039;";
    return char;
  });

const toNumber = (value: number | undefined | null) =>
  Number.isFinite(value ?? Number.NaN) ? Number(value) : 0;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const pct = (value: number, total: number) => {
  if (!total) return 0;
  return clamp((value / total) * 100, 0, 100);
};

const money = (value: number | undefined | null, currency = "COP") => formatCurrency(toNumber(value), currency);

const signedClass = (value: number) => (value < 0 ? "negative" : "positive");

const row = (label: string, value: string, detail = "") => `
  <tr>
    <td>
      <strong>${escapeHtml(label)}</strong>
      ${detail ? `<span>${escapeHtml(detail)}</span>` : ""}
    </td>
    <td class="numeric">${escapeHtml(value)}</td>
  </tr>
`;

const statementRow = (
  label: string,
  detail: string,
  value: number,
  maxValue: number,
  options: { result?: boolean; color?: string } = {}
) => {
  const width = Math.max(pct(Math.abs(value), maxValue), value === 0 ? 0 : 8);
  const color = options.color ?? (value < 0 ? "#dc2626" : "#3159ff");
  return `
    <tr class="${options.result ? "statement-result" : ""}">
      <td>
        <strong>${escapeHtml(label)}</strong>
        <span>${escapeHtml(detail)}</span>
        <div class="mini-bar"><i style="width:${width}%;background:${color}"></i></div>
      </td>
      <td class="statement-value ${signedClass(value)}">${escapeHtml(money(value))}</td>
    </tr>
  `;
};

const metric = (label: string, value: string, detail: string, tone = "blue") => `
  <div class="metric metric-${tone}">
    <span>${escapeHtml(label)}</span>
    <strong>${escapeHtml(value)}</strong>
    <small>${escapeHtml(detail)}</small>
  </div>
`;

const ringMetric = (label: string, value: string, percentage: number, color: string, detail: string) => {
  const safePct = clamp(percentage, 0, 100);
  return `
    <div class="ring-card">
      <svg viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="17" pathLength="100"></circle>
        <circle class="ring-value" cx="22" cy="22" r="17" pathLength="100" style="stroke:${color};stroke-dasharray:${safePct} 100"></circle>
      </svg>
      <div>
        <span>${escapeHtml(label)}</span>
        <strong>${escapeHtml(value)}</strong>
        <small>${escapeHtml(detail)}</small>
      </div>
    </div>
  `;
};

const sparkline = (data: CompanyData["dailyData"], key: "revenue" | "operatingProfit" | "orders", color: string) => {
  const values = data.map((point) => toNumber(point[key]));
  if (values.length === 0) {
    return `<div class="empty-chart">Sin datos diarios para graficar.</div>`;
  }

  const width = 560;
  const height = 180;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const pointList = values.map((value, index) => {
    const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 24) - 12;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const points = pointList.join(" ");
  const firstPoint = pointList[0];
  const lastPoint = pointList[pointList.length - 1] ?? firstPoint;
  const area = `${firstPoint} ${points} ${lastPoint.split(",")[0]},${height} ${firstPoint.split(",")[0]},${height}`;

  return `
    <svg class="sparkline" viewBox="0 0 ${width} ${height}" role="img">
      <polygon points="${area}" fill="${color}" opacity="0.10"></polygon>
      <polyline points="${points}" fill="none" stroke="${color}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></polyline>
    </svg>
  `;
};

const stageLabel = (label: string, value: number, max: number, color: string, detail: string) => {
  const width = Math.max(pct(value, max), value === 0 ? 0 : 7);
  return `
    <div class="funnel-step">
      <div class="funnel-step-head">
        <span>${escapeHtml(label)}</span>
        <strong>${escapeHtml(formatNumber(value))}</strong>
      </div>
      <div class="funnel-track">
        <i style="width:${width}%;background:${color}"></i>
      </div>
      <small>${escapeHtml(detail)}</small>
    </div>
  `;
};

const orderFunnel = (data: CompanyData) => {
  const stages = data.stages;
  const max = Math.max(stages.checkoutViews, stages.ordersCreated, stages.dispatched, stages.delivered, stages.returned, 1);
  return `
    <div class="visual-card">
      <div class="section-title">
        <span>Funnel de órdenes</span>
        <strong>${escapeHtml(formatNumber(stages.ordersCreated))} órdenes del periodo</strong>
      </div>
      ${stageLabel("Checkout", stages.checkoutViews, max, "#3159ff", "Visitas al checkout")}
      ${stageLabel("Órdenes", stages.ordersCreated, max, "#13a37f", money(stages.ordersValue))}
      ${stageLabel("Despachadas", stages.dispatched, max, "#00a6d6", money(stages.dispatchedValue))}
      ${stageLabel("Entregadas", stages.delivered, max, "#16a34a", money(stages.deliveredValue))}
      ${stageLabel("Devueltas", stages.returned, max, "#ef4444", money(stages.returnedValue))}
    </div>
  `;
};

const logisticsBars = (data: CompanyData) => {
  const report = data.logisticsReport;
  if (!report?.sections.length) {
    return `<div class="empty-chart">Sin logística Dropi conectada en este periodo.</div>`;
  }

  const max = Math.max(...report.sections.map((section) => section.count), 1);
  return report.sections
    .map((section) => {
      const width = Math.max(pct(section.count, max), section.count === 0 ? 0 : 8);
      return `
        <div class="status-row">
          <div>
            <strong>${escapeHtml(section.label)}</strong>
            <span>${escapeHtml(`${formatNumber(section.count)} pedidos · ${formatPercentage(section.percentage)}`)}</span>
          </div>
          <div class="status-track"><i style="width:${width}%"></i></div>
          <b>${escapeHtml(money(section.value, report.currency))}</b>
        </div>
      `;
    })
    .join("");
};

const funnelCard = (funnel: FunnelMonthlyRow) => `
  <div class="funnel-card">
    <span>${escapeHtml(funnel.productName || "Sin producto")}</span>
    <strong>${escapeHtml(funnel.funnelName)}</strong>
    <div class="funnel-card-grid">
      <div>
        <small>Recaudo</small>
        <b>${escapeHtml(money(funnel.revenue))}</b>
      </div>
      <div>
        <small>Utilidad</small>
        <b class="${signedClass(funnel.profit)}">${escapeHtml(money(funnel.profit))}</b>
      </div>
      <div>
        <small>Margen</small>
        <b>${escapeHtml(formatPercentage(funnel.marginPct))}</b>
      </div>
    </div>
  </div>
`;

const funnelTableRows = (funnels: CompanyData["funnels"]) =>
  funnels
    .map((funnel) => `
      <tr>
        <td>${escapeHtml(funnel.funnelName)}</td>
        <td>${escapeHtml(funnel.productName || "Sin producto")}</td>
        <td class="numeric">${escapeHtml(money(funnel.revenue))}</td>
        <td class="numeric">${escapeHtml(money(funnel.adInvestment))}</td>
        <td class="numeric ${signedClass(funnel.profit)}">${escapeHtml(money(funnel.profit))}</td>
        <td class="numeric">${escapeHtml(formatPercentage(funnel.marginPct))}</td>
      </tr>
    `)
    .join("");

export function exportCompanyReport(data: CompanyData): boolean {
  const period = data.period;
  const periodLabel = period
    ? `${period.label} · ${formatDateRange(period.from, period.to)}`
    : "Periodo seleccionado";
  const collectedRevenue = data.collectedRevenue ?? data.totalRevenue;
  const netProfitPct = collectedRevenue > 0 ? (data.netProfit / collectedRevenue) * 100 : 0;
  const operatingProfitPct = collectedRevenue > 0 ? (data.operatingProfit / collectedRevenue) * 100 : 0;
  const deliveryRate = data.stages.ordersCreated > 0 ? (data.stages.delivered / data.stages.ordersCreated) * 100 : 0;
  const codCollected = Math.max(data.revenueByPaymentMethod.collected - data.revenueByPaymentMethod.prepaid, 0);
  const costsMax = Math.max(
    collectedRevenue,
    data.totalProductCosts,
    data.totalLogisticsCosts,
    data.totalAdInvestment,
    data.totalFixedCosts,
    Math.abs(data.operatingProfit),
    Math.abs(data.netProfit),
    1
  );
  const bestFunnels = [...data.funnels]
    .filter((funnel) => funnel.revenue > 0 || funnel.profit !== 0)
    .sort((a, b) => b.profit - a.profit)
    .slice(0, 6);
  const attentionFunnels = [...data.funnels]
    .filter((funnel) => funnel.profit < 0 || funnel.marginPct < 10 || (funnel.adInvestment > 0 && funnel.revenue === 0))
    .sort((a, b) => a.profit - b.profit)
    .slice(0, 6);
  const allFunnels = data.funnels.slice(0, 25);

  const reportHtml = `
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Reporte Fluxi Finance</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            background: #edf2ff;
            color: #121826;
            font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          }
          main {
            width: 980px;
            margin: 28px auto;
            background: #ffffff;
            border: 1px solid #dbe4ff;
            border-radius: 22px;
            box-shadow: 0 24px 70px rgba(34, 45, 112, .14);
            overflow: hidden;
          }
          .hero {
            background: #111d4a;
            color: #ffffff;
            padding: 34px 38px 28px;
          }
          .hero-top {
            align-items: flex-start;
            display: flex;
            gap: 28px;
            justify-content: space-between;
          }
          .eyebrow {
            color: #9fb7ff;
            display: block;
            font-size: 11px;
            font-weight: 800;
            letter-spacing: .12em;
            text-transform: uppercase;
          }
          h1 {
            font-size: 34px;
            line-height: 1.05;
            margin: 8px 0 10px;
            max-width: 620px;
          }
          .period, .generated {
            color: #c9d7ff;
            font-size: 13px;
            line-height: 1.5;
          }
          .generated { min-width: 170px; text-align: right; }
          .hero-metrics {
            display: grid;
            gap: 12px;
            grid-template-columns: repeat(4, 1fr);
            margin-top: 28px;
          }
          .metric {
            background: rgba(255,255,255,.10);
            border: 1px solid rgba(255,255,255,.18);
            border-radius: 16px;
            min-height: 118px;
            padding: 16px;
          }
          .metric span, .ring-card span, .soft-card span {
            display: block;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: .08em;
            text-transform: uppercase;
          }
          .metric span { color: #b7c7ff; }
          .metric strong {
            display: block;
            font-size: 21px;
            line-height: 1.15;
            margin-top: 10px;
          }
          .metric small {
            color: #dbe5ff;
            display: block;
            font-size: 11px;
            line-height: 1.35;
            margin-top: 7px;
          }
          .report-body { padding: 28px 34px 36px; }
          .grid-2 {
            display: grid;
            gap: 16px;
            grid-template-columns: 1.1fr .9fr;
          }
          .grid-3 {
            display: grid;
            gap: 12px;
            grid-template-columns: repeat(3, 1fr);
          }
          .visual-card, .table-card {
            border: 1px solid #e3e8f6;
            border-radius: 18px;
            padding: 18px;
            background: #ffffff;
            break-inside: avoid;
          }
          .visual-card.tint {
            background: #f7f9ff;
          }
          .section-title {
            align-items: flex-start;
            display: flex;
            gap: 16px;
            justify-content: space-between;
            margin-bottom: 14px;
          }
          .section-title span {
            color: #64748b;
            font-size: 11px;
            font-weight: 900;
            letter-spacing: .09em;
            text-transform: uppercase;
          }
          .section-title strong {
            color: #101828;
            font-size: 14px;
            text-align: right;
          }
          .ring-grid {
            display: grid;
            gap: 12px;
            grid-template-columns: repeat(3, 1fr);
            margin-bottom: 16px;
          }
          .ring-card {
            align-items: center;
            background: #f8fafc;
            border: 1px solid #e7edf8;
            border-radius: 14px;
            display: flex;
            gap: 12px;
            padding: 12px;
          }
          .ring-card svg {
            flex: 0 0 54px;
            height: 54px;
            transform: rotate(-90deg);
            width: 54px;
          }
          .ring-card circle {
            fill: none;
            stroke: #e4e9f5;
            stroke-width: 6;
          }
          .ring-card .ring-value {
            stroke-linecap: round;
          }
          .ring-card span, .soft-card span { color: #64748b; }
          .ring-card strong {
            color: #101828;
            display: block;
            font-size: 17px;
            margin-top: 4px;
          }
          .ring-card small {
            color: #64748b;
            display: block;
            font-size: 10px;
            margin-top: 3px;
          }
          .sparkline {
            display: block;
            height: 190px;
            width: 100%;
          }
          .mini-bar, .funnel-track, .status-track {
            background: #e8edf7;
            border-radius: 999px;
            height: 8px;
            margin-top: 8px;
            overflow: hidden;
            width: 100%;
          }
          .mini-bar i, .funnel-track i, .status-track i {
            border-radius: inherit;
            display: block;
            height: 100%;
          }
          .funnel-step {
            margin-top: 13px;
          }
          .funnel-step-head {
            align-items: center;
            display: flex;
            justify-content: space-between;
          }
          .funnel-step-head span {
            color: #344054;
            font-size: 13px;
            font-weight: 800;
          }
          .funnel-step-head strong {
            color: #101828;
            font-size: 13px;
          }
          .funnel-step small {
            color: #667085;
            display: block;
            font-size: 10px;
            margin-top: 5px;
          }
          h2 {
            color: #101828;
            font-size: 18px;
            margin: 26px 0 12px;
          }
          table {
            border-collapse: collapse;
            font-size: 12px;
            width: 100%;
          }
          th {
            background: #f7f9ff;
            color: #64748b;
            font-size: 10px;
            letter-spacing: .07em;
            text-transform: uppercase;
          }
          th, td {
            border-bottom: 1px solid #edf1f8;
            padding: 11px 9px;
            text-align: left;
            vertical-align: top;
          }
          td strong {
            color: #111827;
            display: block;
            font-size: 13px;
          }
          td span {
            color: #64748b;
            display: block;
            font-size: 11px;
            margin-top: 3px;
          }
          .numeric {
            font-variant-numeric: tabular-nums;
            text-align: right;
            white-space: nowrap;
          }
          .statement-value {
            color: #111827;
            font-size: 14px;
            font-weight: 900;
            width: 190px;
          }
          .statement-result td {
            background: #f7f9ff;
            border-top: 2px solid #dbe4ff;
          }
          .positive { color: #047857 !important; }
          .negative { color: #b42318 !important; }
          .soft-card {
            background: #f8fafc;
            border: 1px solid #e7edf8;
            border-radius: 14px;
            padding: 14px;
          }
          .soft-card strong {
            color: #101828;
            display: block;
            font-size: 18px;
            margin-top: 7px;
          }
          .soft-card small {
            color: #64748b;
            display: block;
            font-size: 11px;
            margin-top: 5px;
          }
          .status-row {
            align-items: center;
            display: grid;
            gap: 12px;
            grid-template-columns: 180px 1fr 130px;
            margin-top: 12px;
          }
          .status-row strong {
            color: #101828;
            display: block;
            font-size: 12px;
          }
          .status-row span {
            color: #64748b;
            display: block;
            font-size: 10px;
            margin-top: 3px;
          }
          .status-track i { background: #3159ff; }
          .status-row b {
            color: #101828;
            font-size: 12px;
            text-align: right;
          }
          .funnel-cards {
            display: grid;
            gap: 12px;
            grid-template-columns: repeat(2, 1fr);
          }
          .funnel-card {
            border: 1px solid #e3e8f6;
            border-radius: 16px;
            padding: 14px;
            break-inside: avoid;
          }
          .funnel-card > span {
            color: #64748b;
            display: block;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: .07em;
            text-transform: uppercase;
          }
          .funnel-card > strong {
            color: #101828;
            display: block;
            font-size: 14px;
            line-height: 1.25;
            margin-top: 5px;
          }
          .funnel-card-grid {
            display: grid;
            gap: 8px;
            grid-template-columns: repeat(3, 1fr);
            margin-top: 12px;
          }
          .funnel-card-grid small {
            color: #667085;
            display: block;
            font-size: 10px;
          }
          .funnel-card-grid b {
            color: #101828;
            display: block;
            font-size: 11px;
            margin-top: 3px;
          }
          .empty-chart {
            align-items: center;
            background: #f8fafc;
            border: 1px dashed #cbd5e1;
            border-radius: 14px;
            color: #64748b;
            display: flex;
            font-size: 13px;
            justify-content: center;
            min-height: 150px;
            padding: 20px;
            text-align: center;
          }
          .note {
            color: #64748b;
            font-size: 11px;
            line-height: 1.5;
            margin: 22px 0 0;
          }
          @media print {
            body { background: #ffffff; }
            main {
              border: 0;
              border-radius: 0;
              box-shadow: none;
              margin: 0;
              width: auto;
            }
            .hero {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .visual-card, .table-card, .funnel-card, .ring-card, .soft-card {
              break-inside: avoid;
            }
            @page { margin: 12mm; }
          }
        </style>
      </head>
      <body>
        <main>
          <section class="hero">
            <div class="hero-top">
              <div>
                <span class="eyebrow">Fluxi Finance</span>
                <h1>Reporte visual de tu empresa</h1>
                <div class="period">${escapeHtml(periodLabel)}</div>
              </div>
              <div class="generated">
                Generado<br />
                ${escapeHtml(new Intl.DateTimeFormat("es-CO", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                }).format(new Date()))}
              </div>
            </div>
            <div class="hero-metrics">
              ${metric("Recaudo", money(collectedRevenue), "Órdenes entregadas o pagadas")}
              ${metric("Utilidad operativa", money(data.operatingProfit), `${formatPercentage(operatingProfitPct)} sobre recaudo`)}
              ${metric("Utilidad neta", money(data.netProfit), `${formatPercentage(netProfitPct)} sobre recaudo`)}
              ${metric("CPA real", money(data.cpaTargets.realCpa || data.avgCpa), "Meta invertido / órdenes")}
            </div>
          </section>

          <section class="report-body">
            <div class="ring-grid">
              ${ringMetric("Margen neto", formatPercentage(netProfitPct), netProfitPct, "#3159ff", money(data.netProfit))}
              ${ringMetric("Tasa de entrega", formatPercentage(deliveryRate), deliveryRate, "#16a34a", `${formatNumber(data.stages.delivered)} entregadas`)}
              ${ringMetric("ROAS promedio", `${data.avgRoas.toFixed(2)}x`, Math.min(data.avgRoas * 20, 100), "#f59e0b", money(data.totalAdInvestment))}
            </div>

            <div class="grid-2">
              <div class="visual-card tint">
                <div class="section-title">
                  <span>Fotografía diaria</span>
                  <strong>Recaudo del periodo</strong>
                </div>
                ${sparkline(data.dailyData, "revenue", "#3159ff")}
              </div>
              ${orderFunnel(data)}
            </div>

            <h2>PyG visual del periodo</h2>
            <div class="visual-card">
              <table>
                <tbody>
                  ${statementRow("Recaudo total", "Órdenes entregadas o pagadas", collectedRevenue, costsMax, { color: "#3159ff" })}
                  ${statementRow("Costo del producto", "Costo unitario sincronizado desde Dropi", data.totalProductCosts, costsMax, { color: "#f97316" })}
                  ${statementRow("Costos logísticos", "Flete, envío, comisiones y devoluciones sincronizados desde Dropi", data.totalLogisticsCosts, costsMax, { color: "#00a6d6" })}
                  ${statementRow("Inversión publicitaria", "Pauta total de Meta en el periodo", data.totalAdInvestment, costsMax, { color: "#8b5cf6" })}
                  ${statementRow("Utilidad operativa", "Recaudo - costos variables - pauta", data.operatingProfit, costsMax, { color: data.operatingProfit < 0 ? "#dc2626" : "#13a37f" })}
                  ${statementRow("Costos fijos", "Configurados en Fluxi", data.totalFixedCosts, costsMax, { color: "#64748b" })}
                  ${statementRow("Utilidad neta", "Utilidad operativa - costos fijos", data.netProfit, costsMax, { result: true, color: data.netProfit < 0 ? "#dc2626" : "#047857" })}
                </tbody>
              </table>
            </div>

            <h2>Logística Dropi</h2>
            <div class="grid-3">
              <div class="soft-card">
                <span>Dropi total</span>
                <strong>${escapeHtml(`${formatNumber(data.logisticsReport?.totalOrders ?? 0)} pedidos`)}</strong>
                <small>${escapeHtml(money(data.logisticsReport?.matchedValue ?? 0))} sincronizados</small>
              </div>
              <div class="soft-card">
                <span>Sincronizado Fluxi</span>
                <strong>${escapeHtml(`${formatNumber(data.logisticsReport?.matchedOrders ?? 0)} pedidos`)}</strong>
                <small>${escapeHtml(money(data.logisticsReport?.productCost ?? data.totalProductCosts))} producto</small>
              </div>
              <div class="soft-card">
                <span>No sincronizado</span>
                <strong>${escapeHtml(`${formatNumber(data.logisticsReport?.unmatchedOrders ?? 0)} pedidos`)}</strong>
                <small>${escapeHtml(money(data.logisticsReport?.unmatchedValue ?? 0))}</small>
              </div>
            </div>
            <div class="visual-card" style="margin-top:12px">
              <div class="section-title">
                <span>Estados logísticos</span>
                <strong>Secciones completas del imprimible</strong>
              </div>
              ${logisticsBars(data)}
            </div>

            <h2>Marketing consolidado</h2>
            <div class="grid-3">
              <div class="soft-card">
                <span>Inversión Meta</span>
                <strong>${escapeHtml(money(data.totalAdInvestment))}</strong>
                <small>Total invertido en pauta</small>
              </div>
              <div class="soft-card">
                <span>CPA promedio</span>
                <strong>${escapeHtml(money(data.avgCpa))}</strong>
                <small>Costo promedio por adquisición</small>
              </div>
              <div class="soft-card">
                <span>Pauta sin embudo</span>
                <strong>${escapeHtml(money(data.unassignedAdInvestment))}</strong>
                <small>Gasto pendiente de explicar</small>
              </div>
            </div>

            <h2>Dinero por medio de pago</h2>
            <div class="table-card">
              <table>
                <tbody>
                  ${row("Contraentrega", money(data.revenueByPaymentMethod.cashOnDelivery), `Recaudado estimado ${money(codCollected)}`)}
                  ${row("Pago anticipado", money(data.revenueByPaymentMethod.prepaid), `Recaudado estimado ${money(data.revenueByPaymentMethod.prepaid)}`)}
                  ${row("Otros medios", money(data.revenueByPaymentMethod.other), "Órdenes sin medio clasificado")}
                  ${row("Total recaudado estimado", money(data.revenueByPaymentMethod.collected), "Anticipado + contraentrega entregado")}
                </tbody>
              </table>
            </div>

            <h2>Mejores embudos</h2>
            <div class="funnel-cards">
              ${bestFunnels.length ? bestFunnels.map(funnelCard).join("") : '<div class="empty-chart">Sin embudos ganadores en este periodo.</div>'}
            </div>

            <h2>Embudos que necesitan atención</h2>
            <div class="funnel-cards">
              ${attentionFunnels.length ? attentionFunnels.map(funnelCard).join("") : '<div class="empty-chart">Sin embudos críticos en este periodo.</div>'}
            </div>

            <h2>Tabla general de embudos</h2>
            <div class="table-card">
              <table>
                <thead>
                  <tr>
                    <th>Embudo</th>
                    <th>Producto</th>
                    <th class="numeric">Recaudo</th>
                    <th class="numeric">Pauta</th>
                    <th class="numeric">Utilidad</th>
                    <th class="numeric">Margen</th>
                  </tr>
                </thead>
                <tbody>${funnelTableRows(allFunnels) || '<tr><td colspan="6">Sin embudos con datos en este periodo.</td></tr>'}</tbody>
              </table>
            </div>

            <p class="note">
              Este informe usa órdenes de Fluxi, inversión publicitaria de Meta, costos sincronizados de Dropi y costos fijos configurados en Fluxi.
            </p>
          </section>
        </main>
        <script>
          window.addEventListener("load", () => {
            setTimeout(() => window.print(), 250);
          });
        </script>
      </body>
    </html>
  `;

  const reportWindow = window.open("about:blank", "_blank");
  if (reportWindow) {
    reportWindow.document.write(reportHtml);
    reportWindow.document.close();
    reportWindow.focus();
    return true;
  }

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const frameDocument = iframe.contentDocument;
  if (!frameDocument) {
    iframe.remove();
    return false;
  }

  frameDocument.open();
  frameDocument.write(reportHtml);
  frameDocument.close();

  window.setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    window.setTimeout(() => iframe.remove(), 1000);
  }, 150);

  return true;
}
